import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Field, TextField, Choice, formatDocNumber } from "./fields";
import { Input } from "@/components/ui/input";
import {
  UploadCloud, FileText, CheckCircle2, X, Loader2, Camera, ShieldCheck,
} from "lucide-react";

const MAX_MB = 8;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const DOC_TYPES = [
  { id: "passport", label: "Passport", numberLabel: "Passport number", hint: "Exactly as printed on the photo page", expiry: true },
  { id: "id_card", label: "National ID", numberLabel: "National ID number", hint: "The number on the front of your ID card", expiry: true },
  { id: "driving_license", label: "Driving licence", numberLabel: "Licence number", hint: "As shown on the front of your licence", expiry: true },
];

/** Document upload: conditional fields per document type, drag & drop, preview,
 *  size/format validation and an explicit success state. */
export const DocumentUpload = ({ docType, setDocType, meta, setMeta, onUploaded, beforeUpload }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const active = DOC_TYPES.find((d) => d.id === docType) || DOC_TYPES[0];

  const accept = (f) => {
    setError(null);
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      setError("Use a JPG, PNG or PDF file."); return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`That file is ${(f.size / 1048576).toFixed(1)} MB — the limit is ${MAX_MB} MB.`); return;
    }
    setFile(f);
    setDone(false);
    setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };

  const clear = () => { setFile(null); setPreview(null); setDone(false); setError(null); };

  const numberError = meta.doc_number && meta.doc_number.length < 5 ? "That number looks too short." : null;
  const expiryError = meta.doc_expiry && new Date(meta.doc_expiry) < new Date()
    ? "This document has expired — please use a valid one." : null;

  const submit = async () => {
    if (!file || numberError || expiryError || !meta.doc_number) {
      setError(!meta.doc_number ? `Enter your ${active.numberLabel.toLowerCase()}.` : error);
      return;
    }
    setBusy(true);
    try {
      if (beforeUpload) await beforeUpload();
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post("/kyc/document", fd);
      setDone(true);
      toast.success("Uploaded successfully", { description: "Your document is now with our compliance team." });
      onUploaded?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="kyc-upload-panel">
      <Field label="Which document are you using?" required testid="kyc-doc-type-field">
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map((d) => (
            <Choice key={d.id} active={docType === d.id} onClick={() => setDocType(d.id)} testid={`doc-type-${d.id}`}>
              {d.label}
            </Choice>
          ))}
        </div>
      </Field>

      {/* Conditional: only the fields that belong to the chosen document */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={active.numberLabel} hint={active.hint} required error={numberError} testid="kyc-doc-number">
          <TextField value={meta.doc_number || ""} onChange={(v) => setMeta({ ...meta, doc_number: v })}
            format={formatDocNumber} placeholder={docType === "passport" ? "e.g. A1234567" : "e.g. 28412345678"}
            data-testid="kyc-doc-number-input" />
        </Field>
        {active.expiry && (
          <Field label="Expiry date" hint="As printed on the document" required error={expiryError} testid="kyc-doc-expiry">
            <Input type="date" value={meta.doc_expiry || ""} onChange={(e) => setMeta({ ...meta, doc_expiry: e.target.value })}
              className="h-11 rounded-xl" data-testid="kyc-doc-expiry-input" />
          </Field>
        )}
      </div>

      {/* Drop zone */}
      {!file ? (
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); accept(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="kyc-dropzone"
          className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            drag ? "border-[color:var(--tharwy-lime-700)] bg-[color:var(--tharwy-lime)]/10" : "border-border hover:border-[color:var(--tharwy-lime-700)]/60 hover:bg-accent/40"}`}>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center">
            <UploadCloud className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="mt-4 text-sm font-semibold">Drag your {active.label.toLowerCase()} here, or tap to choose</div>
          <p className="text-xs text-muted-foreground mt-1.5">JPG, PNG or PDF · up to {MAX_MB} MB · both sides if it's a card</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border p-4 flex items-center gap-4" data-testid="kyc-file-preview">
          {preview ? (
            <img src={preview} alt="Document preview" className="h-20 w-28 rounded-xl object-cover border border-border" />
          ) : (
            <div className="h-20 w-28 rounded-xl bg-secondary flex items-center justify-center"><FileText className="h-7 w-7 text-muted-foreground" /></div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{file.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{(file.size / 1048576).toFixed(2)} MB</div>
            {done && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid="kyc-upload-success">
                <CheckCircle2 className="h-4 w-4" /> Uploaded successfully
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={clear} data-testid="kyc-file-remove"><X className="h-4 w-4" /></Button>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
        capture="environment" className="hidden" data-testid="kyc-file-input"
        onChange={(e) => accept(e.target.files?.[0])} />

      {error && <p className="text-xs font-medium text-destructive" data-testid="kyc-upload-error">{error}</p>}

      {!done && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" className="h-12 rounded-full gap-2 sm:w-auto w-full"
            onClick={() => inputRef.current?.click()} data-testid="kyc-choose-file">
            <Camera className="h-4 w-4" /> {file ? "Choose another" : "Take photo / choose file"}
          </Button>
          <Button className="h-12 rounded-full gap-2 flex-1" onClick={submit} disabled={busy || !file}
            data-testid="kyc-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {busy ? "Uploading securely…" : "Submit document"}
          </Button>
        </div>
      )}
    </div>
  );
};
